<template>
  <footer ref="footerEl">
    <div class="footer-container">
      <div class="footer-container-box fade-up-animation">
        <nuxt-link to="/" class="vortec-logo" aria-label="Go to homepage">
          <ClientOnly>
            <!-- three.js is heavy; only load its chunk once the footer nears the viewport. -->
            <LazyVortecLogoAnimation v-if="showLogo" />
          </ClientOnly>
        </nuxt-link>

        <nuxt-link to="mailto:hello@vortec.io" class="text-animation" aria-label="Send us an email">
          <p class="text-animation-visible list-text-medium">hello@vortec.io</p>
          <p class="text-animation-hidden list-text-medium">hello@vortec.io</p>
        </nuxt-link>
      </div>

      <div class="footer-container-links">
        <div class="footer-container-box fade-up-animation">
          <h3 class="heading-5-list-bold">Navigation</h3>
          <div class="footer-container-box-column">
            <nuxt-link to="/" class="text-animation" aria-label="Navigate to home section">
              <p class="text-animation-visible list-text-medium">Home</p>
              <p class="text-animation-hidden list-text-medium">Home</p>
            </nuxt-link>
            <nuxt-link to="/about" class="text-animation" aria-label="Navigate to about section">
              <p class="text-animation-visible list-text-medium">About</p>
              <p class="text-animation-hidden list-text-medium">About</p>
            </nuxt-link>
            <nuxt-link to="/projects" class="text-animation" aria-label="Navigate to projects page">
              <p class="text-animation-visible list-text-medium">Projects</p>
              <p class="text-animation-hidden list-text-medium">Projects</p>
            </nuxt-link>
            <nuxt-link to="/careers" class="text-animation" aria-label="Navigate to careers page">
              <p class="text-animation-visible list-text-medium">Careers</p>
              <p class="text-animation-hidden list-text-medium">Careers</p>
            </nuxt-link>
            <nuxt-link to="/faq" class="text-animation" aria-label="Navigate to FAQ section">
              <p class="text-animation-visible list-text-medium">FAQ</p>
              <p class="text-animation-hidden list-text-medium">FAQ</p>
            </nuxt-link>
            <nuxt-link to="/contact" class="text-animation" aria-label="Navigate to contact section">
              <p class="text-animation-visible list-text-medium">Contact</p>
              <p class="text-animation-hidden list-text-medium">Contact</p>
            </nuxt-link>
            <nuxt-link to="/referrals" class="text-animation" aria-label="Navigate to referrals section">
              <p class="text-animation-visible list-text-medium">Referrals</p>
              <p class="text-animation-hidden list-text-medium">Referrals</p>
            </nuxt-link>
          </div>
        </div>
        <div class="footer-container-box fade-up-animation">
          <h3 class="heading-5-list-bold">Projects</h3>
          <div class="footer-container-box-column">
            <nuxt-link
              v-for="project in liveProjects"
              :key="project.slug"
              :to="project.link"
              target="_blank"
              rel="noopener noreferrer"
              class="text-animation"
              :aria-label="`Visit ${project.name}`">
              <p class="text-animation-visible list-text-medium">{{ project.name }}</p>
              <p class="text-animation-hidden list-text-medium">{{ project.name }}</p>
            </nuxt-link>
          </div>
        </div>
        <div class="footer-container-box fade-up-animation">
          <h3 class="heading-5-list-bold">Follow us</h3>
          <div class="footer-container-box-column">
            <nuxt-link
              to="https://www.linkedin.com/company/vortec-io"
              target="_blank"
              class="text-animation"
              aria-label="Visit our LinkedIn page">
              <p class="text-animation-visible list-text-medium">LinkedIn</p>
              <p class="text-animation-hidden list-text-medium">LinkedIn</p>
            </nuxt-link>
            <nuxt-link
              to="https://www.instagram.com/vortec.io"
              target="_blank"
              class="text-animation"
              aria-label="Visit our Instagram page">
              <p class="text-animation-visible list-text-medium">Instagram</p>
              <p class="text-animation-hidden list-text-medium">Instagram</p>
            </nuxt-link>
          </div>
        </div>
      </div>
    </div>
    <div class="footer-bottom">
      <div class="footer-bottom-text fade-up-animation">
        <p class="medium-text-light">© VORTEK INOVATIONS D.O.O. {{ new Date().getFullYear() }} All rights reserved.</p>
        <p class="medium-text-light">Njudehliska 6/1-13, Karposh, Skopje, North Macedonia</p>
        <p class="medium-text-light">Company number: 7757832 | VAT Number: 4057024570335</p>
        <p class="medium-text-light">D-U-N-S: 499334484</p>
      </div>
      <div class="footer-bottom-links fade-up-animation">
        <nuxt-link to="/privacy-policy" class="medium-text-light text-animation" aria-label="Read our Privacy Policy">
          <p class="text-animation-visible medium-text-light">Privacy Policy</p>
          <p class="text-animation-hidden medium-text-light">Privacy Policy</p>
        </nuxt-link>
        <nuxt-link
          to="/terms-and-conditions"
          class="medium-text-light text-animation"
          aria-label="Read our Terms and Conditions">
          <p class="text-animation-visible medium-text-light">Terms and Conditions</p>
          <p class="text-animation-hidden medium-text-light">Terms and Conditions</p>
        </nuxt-link>
        <nuxt-link to="/cookie-policy" class="medium-text-light text-animation" aria-label="Read our Cookie Policy">
          <p class="text-animation-visible medium-text-light">Cookie Policy</p>
          <p class="text-animation-hidden medium-text-light">Cookie Policy</p>
        </nuxt-link>
      </div>
    </div>
  </footer>
</template>

<script lang="ts" setup>
import { useFadeUpObserver } from '@/composables/useFadeUpObserver';
import { caseStudies } from '@/data/caseStudies';

useFadeUpObserver();

// Projects with a live, public URL (skip those whose link is a placeholder "#").
const liveProjects = computed(() => caseStudies.filter((study) => study.link && study.link !== '#'));

// Defer the three.js logo chunk until the footer is about to be seen.
const footerEl = useTemplateRef<HTMLElement>('footerEl');
const showLogo = ref(false);

onMounted(() => {
  if (!footerEl.value) {
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        showLogo.value = true;
        observer.disconnect();
      }
    },
    { rootMargin: '300px' }
  );

  observer.observe(footerEl.value);

  onBeforeUnmount(() => observer.disconnect());
});
</script>
